import { Controller } from "@hotwired/stimulus"

import { overlayContainer } from "../lib/overlayContainer"

const PLAIN_CLASSES = ["text-reset", "text-decoration-none"]
const TOGGLE_ATTRIBUTES = ["data-bs-toggle", "data-bs-target", "role", "tabindex", "aria-controls", "aria-expanded"]

export default class extends Controller {
  static targets = ["line", "collapse"]
  static values = {
    moreTitle: { type: String, default: "Show more" },
    lessTitle: { type: String, default: "Show less" },
  }

  connect() {
    this.onShow = () => this.tooltip?.setContent({ ".tooltip-inner": this.lessTitleValue })
    this.onHide = () => this.tooltip?.setContent({ ".tooltip-inner": this.moreTitleValue })
    this.onHidden = () => this.refresh()
    this.collapseTarget.addEventListener("show.bs.collapse", this.onShow)
    this.collapseTarget.addEventListener("hide.bs.collapse", this.onHide)
    this.collapseTarget.addEventListener("hidden.bs.collapse", this.onHidden)

    this.observer = new ResizeObserver(() => this.refresh())
    this.observer.observe(this.lineTarget)
    this.refresh()
  }

  disconnect() {
    this.observer.disconnect()
    this.collapseTarget.removeEventListener("show.bs.collapse", this.onShow)
    this.collapseTarget.removeEventListener("hide.bs.collapse", this.onHide)
    this.collapseTarget.removeEventListener("hidden.bs.collapse", this.onHidden)
    this.disable()
  }

  refresh() {
    const overflowing = this.lineTarget.scrollWidth > this.lineTarget.clientWidth
    // an open reason must stay collapsible even after a resize lets its line fit
    if (overflowing || this.expanded) {
      this.enable()
    } else {
      this.disable()
    }
  }

  get expanded() {
    return this.collapseTarget.classList.contains("show")
  }

  enable() {
    if (this.tooltip) return

    const line = this.lineTarget
    line.setAttribute("data-bs-toggle", "collapse")
    line.setAttribute("data-bs-target", `#${this.collapseTarget.id}`)
    line.setAttribute("role", "button")
    line.setAttribute("tabindex", "0")
    line.setAttribute("aria-controls", this.collapseTarget.id)
    line.setAttribute("aria-expanded", String(this.expanded))
    line.classList.remove(...PLAIN_CLASSES)
    line.style.removeProperty("cursor")
    this.tooltip = new bootstrap.Tooltip(line, {
      container: overlayContainer(line),
      // a focus trigger keeps the tooltip up after the click until focus moves elsewhere
      trigger: "hover",
      title: this.expanded ? this.lessTitleValue : this.moreTitleValue,
    })
  }

  disable() {
    if (!this.tooltip) return

    const line = this.lineTarget
    this.tooltip.hide()
    this.tooltip.dispose()
    this.tooltip = null
    TOGGLE_ATTRIBUTES.forEach((attribute) => line.removeAttribute(attribute))
    line.classList.add(...PLAIN_CLASSES)
    // the global `a { cursor: pointer }` would otherwise mark plain text as clickable
    line.style.cursor = "auto"
  }
}
